import {
    DataTable as PrimeReactDataTable,
} from 'primereact/datatable';
import React, {
    Children,
    cloneElement,
    forwardRef,
    isValidElement,
    useCallback,
    useEffect,
    useImperativeHandle,
    useMemo,
    useRef,
    useState,
} from 'react';
import type {
    Dispatch,
    ForwardedRef,
    ReactElement,
    ReactNode,
    SetStateAction,
} from 'react';
import { useTranslation } from 'react-i18next';

import FilterPanel from '../filterpanel2/FilterPanel';
import { FILTER_TYPES } from '../datatable/DataTable';
import { DataTableCriteriaModel } from '../datatable2/models/DataTableCriteriaModel';
import { DataTableViewModel } from '../datatable2/viewmodels/DataTableViewModel';
import type { FilterType } from '../datatable2/types/FilterType';

// @ts-ignore
import filterIcon from '../../assets/images/datatable/filter-alt.svg';
// @ts-ignore
import removeFilterIcon from '../../assets/images/datatable/filter-alt-off2.svg';

// @ts-ignore
import './DataTableSubtable.scss';

export type SubtableSearchCriteria = {
    first: number;
    rows: number;
    sortField: string | null;
    sortOrder: number | null;
    filters?: Record<string, unknown>;
    timestamp?: number;
};

export type SubtableFetchResult<TRow> = {
    data: TRow[];
    totalRecords: number;
};

export type DataTableSubtableHandle = {
    refresh: () => void;
};

export type DataTableSubtableFilterConfig = {
    fieldName: string;
    label: string;
    type: FilterType;
    params?: unknown;
    apiParamName?: string;
    apiValueConverter?: (value: unknown) => unknown;
    apiValueFromConverter?: (value: unknown) => unknown;
    apiValueToConverter?: (value: unknown) => unknown;
};

export type DataTableSubtableProps<
    TRow,
    TCriteria extends SubtableSearchCriteria = SubtableSearchCriteria,
> = {
    fetchData: (
        criteria: TCriteria,
    ) => Promise<SubtableFetchResult<TRow>>;

    searchCriteria: TCriteria;

    setSearchCriteria: Dispatch<SetStateAction<TCriteria>>;

    children: ReactNode;

    /**
     * Uses the same configuration shape as DataTable2.
     *
     * A column with a matching config gets the Figma funnel icon and opens
     * filterpanel2/FilterPanel. For backwards compatibility, a column marked
     * `sortable` but missing from this array receives an automatically-created
     * TEXT filter. Sorting is never enabled by this component.
     */
    filterConfig?: DataTableSubtableFilterConfig[];

    dataKey?: string;
    className?: string;
    emptyMessage?: string;
    rowClassName?: (row: TRow) => string | object | undefined;
    showFilterIcons?: boolean;
};

type PrimeColumnPropsLike = {
    field?: string;
    sortField?: string;
    header?: ReactNode | ((options: unknown) => ReactNode);
    sortable?: boolean;
    headerClassName?: string;
};

type FilterPanelState = {
    fieldName: string | null;
    filterType?: FilterType;
    filterParams?: unknown;
};

const joinClassNames = (
    ...classNames: Array<string | false | null | undefined>
): string => classNames.filter(Boolean).join(' ');

const getHeaderContent = (
    header: PrimeColumnPropsLike['header'],
    columnProps: PrimeColumnPropsLike,
): ReactNode => {
    if (typeof header !== 'function') {
        return header ?? null;
    }

    try {
        return header({
            props: columnProps,
        });
    } catch {
        return null;
    }
};

const getHeaderLabel = (
    header: ReactNode,
    fallback: string,
): string => {
    if (typeof header === 'string' || typeof header === 'number') {
        return String(header);
    }

    return fallback;
};

const getNestedValue = (
    row: unknown,
    fieldName: string,
): unknown => {
    if (!row || typeof row !== 'object' || !fieldName) {
        return undefined;
    }

    return fieldName.split('.').reduce<unknown>((current, key) => {
        if (
            current === null ||
            current === undefined ||
            typeof current !== 'object'
        ) {
            return undefined;
        }

        return (current as Record<string, unknown>)[key];
    }, row);
};

const normalizeOption = (
    value: unknown,
): { id: string; label: string } | null => {
    if (value === null || value === undefined || value === '') {
        return null;
    }

    if (
        typeof value === 'object' &&
        'id' in value &&
        'label' in value
    ) {
        const option = value as {
            id: unknown;
            label: unknown;
        };

        return {
            id: String(option.id),
            label: String(option.label),
        };
    }

    return {
        id: String(value),
        label: String(value),
    };
};

const DataTableSubtableInner = <
    TRow,
    TCriteria extends SubtableSearchCriteria = SubtableSearchCriteria,
>(
    {
        fetchData,
        searchCriteria,
        setSearchCriteria,
        children,
        filterConfig = [],
        dataKey = 'id',
        className = '',
        emptyMessage,
        rowClassName,
        showFilterIcons = true,
    }: DataTableSubtableProps<TRow, TCriteria>,
    ref: ForwardedRef<DataTableSubtableHandle>,
): React.ReactElement => {
    const { t } = useTranslation();

    const filterDialogRef = useRef<any>(null);
    const requestSequenceRef = useRef(0);

    const [data, setData] = useState<TRow[]>([]);
    const [loading, setLoading] = useState(false);
    const [filterPanelState, setFilterPanelState] =
        useState<FilterPanelState>({
            fieldName: null,
        });

    /**
     * Build one stable filter configuration for the component.
     *
     * Explicit filterConfig entries always win. A legacy `sortable` column
     * becomes a TEXT-filter column only when it has no explicit entry.
     * The `sortable` flag is consumed as a compatibility signal and is then
     * disabled on the cloned PrimeReact column.
     */
    const resolvedFilterConfig = useMemo<
        DataTableSubtableFilterConfig[]
    >(() => {
        const result = new Map<
            string,
            DataTableSubtableFilterConfig
        >();

        filterConfig.forEach((config) => {
            result.set(config.fieldName, config);
        });

        Children.forEach(children, (child) => {
            if (!isValidElement(child)) {
                return;
            }

            const column =
                child as ReactElement<PrimeColumnPropsLike>;

            const fieldName =
                typeof column.props.field === 'string'
                    ? column.props.field
                    : null;

            if (
                !fieldName ||
                !column.props.sortable ||
                result.has(fieldName)
            ) {
                return;
            }

            const headerContent = getHeaderContent(
                column.props.header,
                column.props,
            );

            result.set(fieldName, {
                fieldName,
                label: getHeaderLabel(
                    headerContent,
                    fieldName,
                ),
                type: FILTER_TYPES.TEXT as FilterType,
                apiParamName: fieldName,
            });
        });

        return Array.from(result.values());
    }, [children, filterConfig]);

    const filterConfigRef = useRef(resolvedFilterConfig);

    useEffect(() => {
        filterConfigRef.current = resolvedFilterConfig;
    }, [resolvedFilterConfig]);

    const viewModel = useMemo(
        () =>
            new DataTableViewModel(
                resolvedFilterConfig as any,
            ),
        [resolvedFilterConfig],
    );

    const viewModelRef = useRef(viewModel);

    useEffect(() => {
        viewModelRef.current = viewModel;
    }, [viewModel]);

    useImperativeHandle(
        ref,
        () => ({
            refresh: (): void => {
                setSearchCriteria((previous) => ({
                    ...previous,
                    timestamp: Date.now(),
                }));
            },
        }),
        [setSearchCriteria],
    );

    useEffect(() => {
        const requestSequence =
            ++requestSequenceRef.current;

        let active = true;

        const loadData = async (): Promise<void> => {
            setLoading(true);

            try {
                const decoratedCriteria =
                    DataTableCriteriaModel.decorate(
                        searchCriteria,
                        filterConfigRef.current as any,
                    );

                const response = await fetchData(
                    (decoratedCriteria ??
                        searchCriteria) as TCriteria,
                );

                if (
                    !active ||
                    requestSequence !==
                        requestSequenceRef.current
                ) {
                    return;
                }

                const responseData = Array.isArray(
                    response?.data,
                )
                    ? response.data
                    : [];

                setData(
                    viewModelRef.current.normalizeDataDates(
                        responseData,
                    ) as TRow[],
                );
            } catch (error) {
                if (
                    active &&
                    requestSequence ===
                        requestSequenceRef.current
                ) {
                    console.error(
                        'DataTableSubtable::loadData error:',
                        error,
                    );
                    setData([]);
                }
            } finally {
                if (
                    active &&
                    requestSequence ===
                        requestSequenceRef.current
                ) {
                    setLoading(false);
                }
            }
        };

        void loadData();

        return () => {
            active = false;
        };
    }, [fetchData, searchCriteria]);

    const commitFilter = useCallback(
        (
            fieldName: string,
            type: FilterType,
            value: unknown,
        ): void => {
            setSearchCriteria((previous) => {
                const vm = viewModelRef.current;

                const nextFilters = {
                    ...(previous.filters ?? {}),
                    [fieldName]: vm.createFilter(
                        fieldName,
                        type,
                        value,
                    ),
                };

                return {
                    ...previous,
                    first: 0,
                    filters: vm.cleanFilters(nextFilters),
                };
            });
        },
        [setSearchCriteria],
    );

    const applyFilter = useCallback(
        (
            type: FilterType,
            params: unknown,
        ): void => {
            if (!filterPanelState.fieldName) {
                return;
            }

            commitFilter(
                filterPanelState.fieldName,
                type,
                params,
            );
        },
        [
            commitFilter,
            filterPanelState.fieldName,
        ],
    );

    const clearFilter = useCallback(
        (fieldName: string): void => {
            filterDialogRef.current?.hide();

            setSearchCriteria((previous) => {
                const nextFilters = {
                    ...(previous.filters ?? {}),
                };

                delete nextFilters[fieldName];

                return {
                    ...previous,
                    first: 0,
                    filters: nextFilters,
                };
            });
        },
        [setSearchCriteria],
    );

    const getTextFilterParams = useCallback(
        (
            fieldName: string,
        ): {
            value: Array<{
                id: string;
                label: string;
            }>;
            options: Array<{
                id: string;
                label: string;
            }>;
        } => {
            const uniqueOptions = new Map<
                string,
                {
                    id: string;
                    label: string;
                }
            >();

            data.forEach((row) => {
                const option = normalizeOption(
                    getNestedValue(row, fieldName),
                );

                if (option) {
                    uniqueOptions.set(option.id, option);
                }
            });

            const currentFilter = (
                searchCriteria.filters?.[
                    fieldName
                ] as {
                    value?: unknown;
                } | undefined
            )?.value;

            const rawSelectedValues = Array.isArray(
                currentFilter,
            )
                ? currentFilter
                : currentFilter !== null &&
                    currentFilter !== undefined &&
                    currentFilter !== ''
                  ? [currentFilter]
                  : [];

            const selectedValues = rawSelectedValues
                .map(normalizeOption)
                .filter(
                    (
                        option,
                    ): option is {
                        id: string;
                        label: string;
                    } => option !== null,
                );

            selectedValues.forEach((option) => {
                uniqueOptions.set(option.id, option);
            });

            return {
                value: selectedValues,
                options: Array.from(
                    uniqueOptions.values(),
                ),
            };
        },
        [data, searchCriteria.filters],
    );

    const openFilterPanel = useCallback(
        (
            event: React.MouseEvent<HTMLButtonElement>,
            config: DataTableSubtableFilterConfig,
        ): void => {
            event.preventDefault();
            event.stopPropagation();

            /*
             * IMPORTANT:
             * React's event.currentTarget is only guaranteed during the
             * synchronous click handler. Reading it from setTimeout makes it
             * null, which causes PrimeReact's OverlayPanel to open at (0, 0).
             */
            const anchor = event.currentTarget;

            const params =
                config.type === FILTER_TYPES.TEXT
                    ? getTextFilterParams(config.fieldName)
                    : viewModelRef.current.enhanceFilterParams(
                          config.fieldName,
                          config.type,
                          config.params,
                          searchCriteria,
                      );

            setFilterPanelState({
                fieldName: config.fieldName,
                filterType: config.type,
                filterParams: params,
            });

            /*
             * Wait until FilterPanel receives the new type/params, but use the
             * captured DOM element rather than the expired React event.
             */
            window.setTimeout(() => {
                if (!anchor.isConnected) {
                    return;
                }

                const overlayEvent = {
                    currentTarget: anchor,
                    target: anchor,
                    preventDefault: () => undefined,
                    stopPropagation: () => undefined,
                };

                const filterDialog = filterDialogRef.current;

                if (!filterDialog) {
                    return;
                }

                /*
                 * `show` places an already-open panel beside the new icon.
                 * Fall back to `toggle` for FilterPanel implementations that
                 * expose only OverlayPanel.toggle().
                 */
                if (typeof filterDialog.show === 'function') {
                    filterDialog.show(overlayEvent, anchor);
                } else {
                    filterDialog.toggle?.(overlayEvent);
                }
            }, 0);
        },
        [
            getTextFilterParams,
            searchCriteria,
        ],
    );

    const filterConfigByField = useMemo(() => {
        const result = new Map<
            string,
            DataTableSubtableFilterConfig
        >();

        resolvedFilterConfig.forEach((config) => {
            result.set(config.fieldName, config);
        });

        return result;
    }, [resolvedFilterConfig]);

    const decoratedColumns = useMemo(
        () =>
            Children.map(children, (child) => {
                if (!isValidElement(child)) {
                    return child;
                }

                const column =
                    child as ReactElement<PrimeColumnPropsLike>;

                const fieldName =
                    typeof column.props.field === 'string'
                        ? column.props.field
                        : null;

                const headerContent = getHeaderContent(
                    column.props.header,
                    column.props,
                );

                const config = fieldName
                    ? filterConfigByField.get(fieldName)
                    : undefined;

                const isFilterActive =
                    Boolean(fieldName) &&
                    Boolean(
                        searchCriteria.filters &&
                            Object.prototype.hasOwnProperty.call(
                                searchCriteria.filters,
                                fieldName as string,
                            ),
                    );

                const shouldRenderFilter =
                    showFilterIcons &&
                    Boolean(fieldName) &&
                    Boolean(config);

                if (!shouldRenderFilter || !fieldName || !config) {
                    return cloneElement(column, {
                        /**
                         * No sorting anywhere in DataTableSubtable.
                         * The action column and non-filter columns keep their
                         * original header content.
                         */
                        sortable: false,
                    });
                }

                const customHeader = (
                    <div
                        className={joinClassNames(
                            'data-table-subtable__header',
                            isFilterActive &&
                                'data-table-subtable__header--filtered',
                        )}
                    >
                        <span className="data-table-subtable__header-label">
                            {headerContent}
                        </span>

                        <button
                            type="button"
                            className={joinClassNames(
                                'data-table-subtable__filter-button',
                                isFilterActive &&
                                    'data-table-subtable__filter-button--active',
                            )}
                            title={
                                isFilterActive
                                    ? t(
                                          'dataTable.removeFilter',
                                          'Remove filter',
                                      )
                                    : t(
                                          'dataTable.filter',
                                          'Filter',
                                      )
                            }
                            aria-label={
                                isFilterActive
                                    ? t(
                                          'dataTable.removeFilter',
                                          'Remove filter',
                                      )
                                    : t(
                                          'dataTable.filter',
                                          'Filter',
                                      )
                            }
                            aria-pressed={isFilterActive}
                            onClick={(event) => {
                                if (isFilterActive) {
                                    event.preventDefault();
                                    event.stopPropagation();
                                    clearFilter(fieldName);
                                    return;
                                }

                                openFilterPanel(
                                    event,
                                    config,
                                );
                            }}
                        >
                            <img
                                className="data-table-subtable__filter-icon"
                                src={
                                    isFilterActive
                                        ? removeFilterIcon
                                        : filterIcon
                                }
                                alt=""
                                aria-hidden="true"
                            />
                        </button>
                    </div>
                );

                return cloneElement(column, {
                    sortable: false,
                    header: customHeader,
                    headerClassName:
                        joinClassNames(
                            column.props.headerClassName,
                            'data-table-subtable__column-header',
                            isFilterActive &&
                                'data-table-subtable__column-header--filtered',
                        ),
                });
            }),
        [
            children,
            clearFilter,
            filterConfigByField,
            openFilterPanel,
            searchCriteria.filters,
            showFilterIcons,
            t,
        ],
    );

    return (
        <>
            <FilterPanel
                ref={filterDialogRef}
                onSortAsc={false}
                onSortDesc={false}
                type={filterPanelState.filterType}
                params={filterPanelState.filterParams}
                applyFilter={applyFilter}
            />

            <div
                className={joinClassNames(
                    'data-table-subtable',
                    className,
                )}
            >
                <PrimeReactDataTable
                    className="data-table-subtable__table"
                    value={data}
                    dataKey={dataKey}
                    lazy
                    loading={loading}
                    paginator={false}
                    rowClassName={rowClassName}
                    emptyMessage={
                        emptyMessage ??
                        t('dataTable.emptyMessage')
                    }
                    tableStyle={{
                        width: '100%',
                        tableLayout: 'fixed',
                    }}
                >
                    {decoratedColumns}
                </PrimeReactDataTable>
            </div>
        </>
    );
};

const DataTableSubtable = forwardRef(
    DataTableSubtableInner,
) as <
    TRow,
    TCriteria extends SubtableSearchCriteria = SubtableSearchCriteria,
>(
    props: DataTableSubtableProps<
        TRow,
        TCriteria
    > & {
        ref?: ForwardedRef<DataTableSubtableHandle>;
    },
) => React.ReactElement;

export default DataTableSubtable;
